<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <div class="form-section group">
      <span class="icon"><icon-colored-pollen /></span>
      {{ capitalize($t('messages.pollenTargeting')) }}
      <div
        v-if="canBeRemoved"
        class="actions">
        <button
          class="btn btn-danger btn-sm"
          :disabled="isReadOnly"
          @click="clear">
          <icon-delete />
          {{ capitalize($t('actions.remove')) }}
        </button>
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="isMounted"
        class="form-section-content">
        <div class="form-group row">
          <div
            id="edit_creative_pollen"
            class="col-sm-12">
            <pollen-targeting-editor
              :wrapped-in-pane="false"
              :is-read-only="isReadOnly"
              :selected-pollen-conditions="pollenTargetingConditions"
              :targeting-criteria="targetingCriteria"
              @selected-pollen-conditions-changed="(x) => (pollenTargetingConditions = x)" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script type="text/babel">
  import { capitalize } from 'vue';
  import { mapActions } from 'vuex';

  import PollenTargetingEditor from '@/common/components/controls/targeting/pollen/PollenTargetingEditor';
  import TargetingCriteriaType from '@/common/enums/TargetingCriteriaType.enum';
  import IconColoredPollen from '@/common/icons/colored/Pollen';
  import IconDelete from '@/common/icons/monochrome/Delete';
  import { Creative } from '@/common/models';
  import CreativeTargetingCriterion from '@/common/models/creative/CreativeTargetingCriterion';
  import { useTargetingCriteria } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';

  export default {
    components: {
      PollenTargetingEditor,
      IconColoredPollen,
      IconDelete,
    },
    props: {
      creative: {
        type: Creative,
        default: null,
      },
      canBeRemoved: {
        type: Boolean,
        default: false,
      },
      isReadOnly: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['cleared'],
    data() {
      return {
        targetingCriteriaQuery: useTargetingCriteria(),
        isMounted: true,
      };
    },
    computed: {
      targetingCriteria() {
        return this.targetingCriteriaQuery.items;
      },
      pollenTargetingConditions: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const pollenCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.Pollen);
            if (pollenCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === pollenCriterion.id,
              );
              if (criterion != null) {
                return criterion.values.find((x) => x.code !== 'LOCATION').value.split(',');
              }
            }
          }
          return null;
        },
        set(val) {
          const pollenTargetingCriteria = this.getPollenTargetingCriteria(val);
          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [...(pollenTargetingCriteria == null ? [] : pollenTargetingCriteria)],
          });
        },
      },
    },
    async beforeMount() {
      await waitForPendingQueries([this.targetingCriteriaQuery]);
    },
    mounted() {
      this.isMounted = true;
    },
    unmounted() {
      this.isMounted = false;
    },
    methods: {
      capitalize,
      ...mapActions(['updateCreativeTargetingCriteria']),
      getPollenTargetingCriteria(conditions) {
        const criteria = [];
        if (conditions != null && conditions.length !== 0) {
          const pollenCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.Pollen);

          criteria.push(
            new CreativeTargetingCriterion(this.creative.id, pollenCriterion.id, [
              {
                code: 'POLLEN_CATEGORY',
                value: conditions.join(','),
              },
            ]),
          );
        }
        if (criteria.length > 0) {
          return criteria;
        }
        return null;
      },
      clear() {
        this.updateCreativeTargetingCriteria({ creative: this.creative, value: [] });
        this.$emit('cleared');
      },
    },
  };
</script>
